import { FC, useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch } from "../../../hooks";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import DiscardConfirmationModal from "../../common/discard-confirmation-modal/DiscardConfirmationModal";

import {
  addRackAction,
  getallRacksAction,
  updateRackAction,
} from "../../../redux/actions/rackAction";
import { store } from "../../../redux/store";

const schema = Yup.object().shape({
  conveyorName: Yup.string().trim().required("Conveyor name is required"),
  slotCount: Yup.number()
    .typeError("Slot count must be a number")
    .integer("Slot count must be a whole number")
    .min(1, "Slot count must be at least 1")
    .required("Slot count is required"),
});

type FormData = Yup.InferType<typeof schema>;

interface IAddRackDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  populateFor?: {
    id?: number;
    rackName?: string;
    rackId: string;
    rackCapacity?: number;
  };
}

export const AddRackDrawer: FC<IAddRackDrawerProps> = ({
  open,
  toggleDrawer,
  populateFor,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const [loading, setLoading] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      conveyorName: "",
      slotCount: undefined,
    },
  });

  console.log(populateFor?.rackName);

  useEffect(() => {
    if (open) {
      reset({
        conveyorName: populateFor?.rackName ?? "",
        slotCount: populateFor?.rackCapacity,
      });
    }
  }, [open, populateFor, reset]);

  const buildSlots = (count: number) =>
    Array.from({ length: count }, (_, i) => ({
      slotId: i + 1,
      slotName: `slot ${i + 1}`,
    }));

  const handleSave = async (data: FormData) => {
    setLoading(true);
    try {
      const all = store.getState().rack.getAllRacks?.body || [];
      const duplicate = all.some((r: any) => {
        const existingName = (r.rackLocation ?? r.convayorName ?? "")
          .toString()
          .trim()
          .toLowerCase();
        const newName = data.conveyorName.trim().toLowerCase();
        const isSelf = r.id === populateFor?.id;
        return existingName === newName && !isSelf;
      });
      if (duplicate) {
        snackbar.showSnackbar("Conveyor name must be unique!", "error");
        setLoading(false);
        return;
      }

      const slotCount = Number(data.slotCount);
      const slots = buildSlots(slotCount);

      if (populateFor?.id) {
        await dispatch(
          updateRackAction({
            id: populateFor.id,
            convayorName: data.conveyorName.trim(),
            slots,
          })
        ).unwrap();
        snackbar.showSnackbar("Conveyor updated successfully!", "success");
      } else {
        const slots = buildSlots(Number(data.slotCount));
        await dispatch(
          addRackAction({
            convayorName: data.conveyorName.trim(),
            slots,
          })
        ).unwrap();
        snackbar.showSnackbar("Conveyor created successfully!", "success");
      }

      dispatch(getallRacksAction());
      toggleDrawer();
    } catch (e) {
      snackbar.showSnackbar("Failed to save. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAttempt = () => {
    if (isDirty) {
      setExitConfirmOpen(true);
    } else {
      toggleDrawer();
    }
  };

  return (
    <>
    <DiscardConfirmationModal
      open={exitConfirmOpen}
      onKeepEditing={() => setExitConfirmOpen(false)}
      onDiscard={() => { reset(); setExitConfirmOpen(false); toggleDrawer(); }}
    />
    <CustomDrawer
      isOpen={open}
      toggleDrawer={handleCloseAttempt}
      sx={{ width: "28.65rem" }}
    >
      <form onSubmit={handleSubmit(handleSave)}>
        <Box
          sx={{
            paddingInline: "1.857rem",
            paddingTop: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.4rem",
              lineHeight: "1.7rem",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            {populateFor?.id ? "Edit Conveyor" : "Add a New Conveyor"}
          </Typography>

          <Box>
            <Typography>Conveyor Name</Typography>
            <Controller
              name="conveyorName"
              control={control}
              render={({ field, fieldState }) => (
                <CustomTextBox
                  {...field}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                  placeholder="e.g. Main Conveyor"
                />
              )}
            />
          </Box>

          <Box>
            <Typography>Slot Count</Typography>
            <Controller
              name="slotCount"
              control={control}
              render={({ field, fieldState }) => (
                <CustomTextBox
                  {...field}
                  onChange={(e: any) => {
                    const v = e.target.value;

                    const num = String(v).replace(/[^\d]/g, "");
                    field.onChange(num === "" ? "" : Number(num));
                  }}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                  placeholder="e.g. 12"
                />
              )}
            />
          </Box>

          <CustomButton
            sx={{ width: "10rem", height: "2.8rem", marginTop: "0.5rem" }}
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : populateFor?.id ? (
              "Update"
            ) : (
              "Create"
            )}
          </CustomButton>
        </Box>
      </form>
    </CustomDrawer>
    </>
  );
};
